import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function SignaturePad({ onChange, typedDefault = "", kind = "signature", testid = "signature-pad", t = (k) => ({ sign_hint: "Use your finger or stylus to sign", initial_hint: "Use your finger or stylus to write initials" }[k] || k) }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [mode, setMode] = useState("draw");
  const [typed, setTyped] = useState("");
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    c.width = r.width * 2;
    c.height = r.height * 2;
    const ctx = c.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0b1f5c";
  }, [mode]);

  const pos = (e) => { const r = ref.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); ref.current.setPointerCapture(e.pointerId); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); setHasInk(true); };
  const up = () => { if (!drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL("image/png")); };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasInk(false); onChange(null); };

  return (
    <div data-testid={testid}>
      <div className="flex gap-2 mb-3" role="tablist">
        <Button type="button" variant={mode === "draw" ? "default" : "outline"} className="h-12 px-5" onClick={() => { setMode("draw"); onChange(null); }} data-testid={`${testid}-mode-draw`}>{t("Draw")}</Button>
        <Button type="button" variant={mode === "type" ? "default" : "outline"} className="h-12 px-5" onClick={() => { setMode("type"); setTyped(""); onChange(null); }} data-testid={`${testid}-mode-type`}>{t("Type instead")}</Button>
      </div>
      {mode === "draw" ? (
        <div>
          <canvas ref={ref} className={`w-full ${kind === "initials" ? "h-32" : "h-44"} bg-white border-2 border-dashed border-slate-300 rounded-xl touch-none`}
            onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} aria-label={`Draw your ${kind} here`} data-testid={`${testid}-canvas`} />
          <div className="flex justify-between mt-2 text-sm text-slate-500">
            <span>{hasInk ? t("Captured") : t(kind === "initials" ? "initial_hint" : "sign_hint")}</span>
            <button type="button" className="underline" onClick={clear} data-testid={`${testid}-clear`}>{t("Clear")}</button>
          </div>
        </div>
      ) : (
        <div>
          <Input value={typed} placeholder={typedDefault || t(kind === "initials" ? "Your initials" : "Type your full name")}
            onChange={(e) => { setTyped(e.target.value); onChange(e.target.value.trim() ? `typed:${e.target.value.trim()}` : null); }}
            className="h-14 text-xl" aria-label={`Type your ${kind}`} data-testid={`${testid}-typed-input`} />
          {typed && <p className="mt-3 text-3xl text-blue-900" style={{ fontFamily: "Georgia, serif", fontStyle: "italic" }}>{typed}</p>}
        </div>
      )}
    </div>
  );
}
